/**
 * useUnsavedChanges Hook
 *
 * Provides unsaved changes detection for Item and SubItem editors.
 * Handles confirmation dialogs and browser beforeunload warnings.
 *
 * Note: React Router navigation blocking (useBlocker) requires a data router
 * which this app doesn't use. Cancel/Back buttons and beforeunload still work.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import type { ItemDraft, SubItemDraft } from '@/services';
import type { ItemFormData } from '../components/fields';
import {
  hasUnsavedChanges,
  hasSubItemUnsavedChanges,
  type UnsavedChangesResult,
} from '../utils/unsavedChanges';

// ============================================================================
// TYPES
// ============================================================================

interface UseUnsavedChangesOptions {
  /** The current draft state (ItemDraft or SubItemDraft) */
  draft: ItemDraft | SubItemDraft | null;
  /** Function to get current form values */
  getFormData: () => ItemFormData;
  /** Count of pending file attachments */
  pendingAttachmentCount: number;
  /** Whether the hook is enabled (disable during save, etc.) */
  enabled?: boolean;
  /** Type of draft for proper comparison function selection */
  draftType: 'item' | 'subItem';
}

interface UseUnsavedChangesReturn {
  /**
   * Check for unsaved changes and return details
   */
  checkChanges: () => UnsavedChangesResult;

  /**
   * Execute an action only if no unsaved changes, or after user confirmation
   * @param action - The action to execute if confirmed (discard)
   * @param options - Optional configuration including save handler
   */
  confirmIfUnsaved: (
    action: () => void,
    options?: {
      title?: string;
      forceCheck?: boolean;
      onSave?: () => void | Promise<void>;
    }
  ) => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useUnsavedChanges({
  draft,
  getFormData,
  pendingAttachmentCount,
  enabled = true,
  draftType,
}: UseUnsavedChangesOptions): UseUnsavedChangesReturn {
  const { confirmWarning } = useConfirm();
  const enabledRef = useRef(enabled);

  // Keep ref updated
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  /**
   * Check for unsaved changes
   */
  const checkChanges = useCallback((): UnsavedChangesResult => {
    if (!draft) {
      return {
        hasChanges: false,
        changedFields: [],
        hasAddedAttachments: false,
        hasRemovedAttachments: false,
        hasAddedSubItems: false,
        hasRemovedSubItems: false,
        hasReorderedChildren: false,
        summary: '',
      };
    }

    const currentFormData = getFormData();

    if (draftType === 'item') {
      return hasUnsavedChanges(draft as ItemDraft, currentFormData, pendingAttachmentCount);
    } else {
      return hasSubItemUnsavedChanges(draft as SubItemDraft, currentFormData, pendingAttachmentCount);
    }
  }, [draft, getFormData, pendingAttachmentCount, draftType]);

  /**
   * Execute action with optional confirmation for unsaved changes
   */
  const confirmIfUnsaved = useCallback(
    (
      action: () => void,
      options?: {
        title?: string;
        forceCheck?: boolean;
        onSave?: () => void | Promise<void>;
      }
    ) => {
      if (!enabledRef.current && !options?.forceCheck) {
        action();
        return;
      }

      const result = checkChanges();

      if (!result.hasChanges) {
        action();
        return;
      }

      // Build message with details
      let message = 'You have unsaved changes that will be lost.';
      if (result.summary) {
        message = `You have unsaved changes: ${result.summary}. These changes will be lost.`;
      }

      confirmWarning({
        title: options?.title || 'Unsaved Changes',
        message,
        confirmText: 'Discard',
        onConfirm: action,
        onSave: options?.onSave,
      });
    },
    [checkChanges, confirmWarning]
  );

  /**
   * Browser beforeunload warning (handles tab close/refresh)
   * Only warns about pending attachments since other changes are saved to localStorage draft
   */
  useEffect(() => {
    if (!enabled || !draft) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only warn about pending attachments - File objects can't be saved to localStorage
      // Form data and other changes are auto-saved to draft and will be restored
      if (pendingAttachmentCount > 0) {
        e.preventDefault();
        e.returnValue = 'You have attachments waiting to be uploaded. They will be lost if you leave.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, draft, pendingAttachmentCount]);

  return {
    checkChanges,
    confirmIfUnsaved,
  };
}
